import { useState } from "react";
import { Plus, Minus } from "lucide-react";

const faqs = [
  {
    q: "Do we need to have experienced a breach or incident to work with you?",
    a: "No. Most clients come to us before anything has happened, because they have deployed AI, built applications, or launched new systems and realized they have no documentation of what it exposes them to. The best time to do a security review is before a regulator asks for one.",
  },
  {
    q: "How long does an assessment take?",
    a: "Phase 1 typically takes two to four weeks depending on the number of AI applications and the complexity of your environment. We deliver a written proposal within 48 hours of your discovery call with a specific timeline scoped to your situation.",
  },
  {
    q: "Do you work with organizations that have not deployed AI yet?",
    a: "Yes. We can help you build a secure foundation before deployment. But our most common engagement is with organizations that have already deployed AI and need to understand what that deployment has exposed them to.",
  },
  {
    q: "What industries do you serve?",
    a: "Healthcare, manufacturing, financial services, legal, insurance, and any regulated environment where AI failure has real consequences. We go where AI is deployed at scale and where the stakes are high.",
  },
  {
    q: "What is your pricing model?",
    a: "All engagements are scoped and priced based on your specific environment: number of AI applications, regulatory frameworks, complexity of your architecture, and urgency. We deliver a written proposal within 48 hours of your discovery call. We never quote verbally.",
  },
  {
    q: "Do you sign Business Associate Agreements for healthcare clients?",
    a: "Yes. We execute HIPAA-compliant Business Associate Agreements with all healthcare clients before any engagement begins.",
  },
  {
    q: "What makes ArqeraAI different from a compliance consultant or an automation agency?",
    a: "We are neither. Compliance consultants find gaps but do not fix them. Automation agencies build AI systems but do not secure them. ArqeraAI does both. We find what is exposed, fix it, document it for your regulators, and then help you adopt AI safely at scale. Full lifecycle. One firm.",
  },
  {
    q: "Can you help if we are already under regulatory scrutiny?",
    a: "Yes. If you have received an OCR inquiry, are preparing for a HITRUST assessment, or have a board presentation on AI governance upcoming, those are exactly the situations where ArqeraAI moves fastest. Regulatory deadlines create urgency and we work to match it.",
  },
];

function Item({ q, a, open, onClick }: { q: string; a: string; open: boolean; onClick: () => void }) {
  return (
    <div className="border-b border-arq-border">
      <button
        onClick={onClick}
        className="w-full text-left py-5 flex items-start justify-between gap-6 group"
      >
        <span className="font-body text-[16px] font-medium text-arq-text group-hover:text-arq-blue transition-colors">
          {q}
        </span>
        <span className="text-arq-blue flex-shrink-0 mt-1 transition-transform duration-200">
          {open ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        </span>
      </button>
      {open && (
        <div className="pb-5 pr-10 text-arq-muted text-[15px] leading-[1.7]">{a}</div>
      )}
    </div>
  );
}

export default function ArqFAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(4);
  const half = Math.ceil(faqs.length / 2);
  const left = faqs.slice(0, half);
  const right = faqs.slice(half);

  const toggle = (i: number) => setOpenIdx(openIdx === i ? null : i);

  return (
    <section id="faq" className="bg-arq-surface1 py-20 md:py-[120px] px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center mb-16">
          <div className="arq-label mb-6">Common Questions</div>
          <h2 className="font-display font-semibold text-arq-text" style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}>
            What to Expect
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-x-12">
          <div>
            {left.map((f, i) => (
              <Item key={i} q={f.q} a={f.a} open={openIdx === i} onClick={() => toggle(i)} />
            ))}
          </div>
          <div>
            {right.map((f, i) => (
              <Item key={i + half} q={f.q} a={f.a} open={openIdx === i + half} onClick={() => toggle(i + half)} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
